import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Box, 
    Paper, 
    Typography, 
    IconButton, 
    Chip, 
    Collapse, 
    Divider, 
    Stack 
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import { NotificationsAPI } from '../../api/Endpoints/NotificationsAPI';
import { toast } from 'react-toastify';

const DailyNotificationPopup = ({ open, setOpen, data, refreshNotifications }) => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);

    if (!open) return null;

    // Use provided data or fallback to empty arrays
    const siteVisits = data?.sitevisit?.today?.visits || [];
    const followUps = data?.followup?.today?.leads || [];

    const handleToggle = () => setExpanded(!expanded);
    const handleClose = () => setOpen(false);

    const handleMarkAllRead = (type) => { // 'sitevisit' | 'followup'
        NotificationsAPI.getScheduledNotifications(type)
            .then((res) => {
                if (res.data.status) {
                    toast.success(`Marked all ${type} as read`);
                    if (refreshNotifications) refreshNotifications();
                }
            })
            .catch(err => console.error(err));
    };

    const handleMarkAsRead = (type, id) => {
        NotificationsAPI.markAsRead(type, id)
            .then((res) => {
                if (res.data.status) {
                    toast.success(`Marked as read`);
                    if (refreshNotifications) refreshNotifications();
                }
            })
            .catch(err => console.error(err));
    };

    const formatDisplayDate = (date) => {
        if (!date) return 'No date';
        
        const today = new Date().toDateString();
        const itemDate = new Date(date).toDateString();
        
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        if (itemDate === today) return `Today (${formattedDate})`;
        if (new Date(date) < new Date()) return `Overdue (${formattedDate})`;
        
        return formattedDate;
    };

    const getDateColor = (date, isRead) => {
        if (isRead === 1) return '#6B7280';
        const today = new Date().toDateString();
        const itemDate = new Date(date).toDateString();
        if (itemDate === today) return '#2563EB';
        if (new Date(date) < new Date()) return '#DC2626';
        return '#059669';
    };

    return (
        <Paper
            elevation={4}
            sx={{
                position: 'fixed',
                top: 20,
                 right: 20,
               // transform: 'translateX(85%)',
                width: 380,
                zIndex: 9999,
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Header - Click anywhere except close button to expand/collapse */}
            <Box 
                sx={{ 
                    p: 2, 
                    bgcolor: '#f0f9f4', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: '#e6f7ec'
                    }
                }}
                onClick={handleToggle}
            >
                <Box 
                    sx={{ 
                        bgcolor: '#dcfce7', 
                        borderRadius: '50%', 
                        p: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                    }}
                >
                    <NotificationsActiveIcon sx={{ color: '#22c55e' }} />
                </Box>
                
                <Box sx={{ flexGrow: 1 }}>
                    <Typography 
                        variant="subtitle1" 
                        sx={{ 
                            fontWeight: 'bold', 
                            color: '#4b4b6b', 
                            lineHeight: 1.2 
                        }}
                    >
                        Today's Schedule
                    </Typography>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: '#8e8eaa', 
                            display: 'block' 
                        }}
                    >
                        {siteVisits.length} site visits & {followUps.length} follow-up scheduled today
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                    <IconButton 
                        size="small"
                        sx={{ 
                            bgcolor: '#f1f1f5', 
                            '&:hover': { bgcolor: '#e2e2e8' }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggle();
                        }}
                    >
                        {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                    <IconButton 
                        size="small"
                        sx={{ 
                            bgcolor: '#fee2e2', 
                            color: '#ef4444', 
                            '&:hover': { bgcolor: '#fecaca' } 
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClose();
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>

            {/* Content Summary Chips - Also clickable to expand/collapse */}
            <Box 
                sx={{ 
                    p: '12px 16px', 
                    display: 'flex', 
                    gap: 1,
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: '#f5f5f5'
                    }
                }}
                onClick={handleToggle}
            >
                <Chip 
                    icon={<BusinessIcon sx={{ fontSize: '16px !important', color: '#10b981 !important' }} />} 
                    label={`${siteVisits.length} Site Visits`} 
                    variant="outlined"
                    sx={{ 
                        borderRadius: '8px', 
                        color: '#10b981', 
                        borderColor: '#10b981', 
                        bgcolor: '#f0fdf4',
                        fontWeight: 600,
                        fontSize: '12px',
                        '&:hover': {
                            bgcolor: '#e6f7e6'
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
                <Chip 
                    icon={<EventNoteIcon sx={{ fontSize: '16px !important', color: '#6366f1 !important' }} />} 
                    label={`${followUps.length} Follow-up`} 
                    variant="outlined"
                    sx={{ 
                        borderRadius: '8px', 
                        color: '#6366f1', 
                        borderColor: '#6366f1', 
                        bgcolor: '#eef2ff',
                        fontWeight: 600,
                        fontSize: '12px',
                        '&:hover': {
                            bgcolor: '#e2e8ff'
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            </Box>

            {/* Expandable List Content */}
            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ p: 2, overflowY: 'auto', maxHeight: '400px' }}>
                    
                    {siteVisits.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: '#10b981', 
                                        fontWeight: 'bold', 
                                        display: 'block'
                                    }}
                                >
                                    Site Visits
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    onClick={() => handleMarkAllRead('sitevisit')}
                                    sx={{ color: '#10b981', cursor: 'pointer', display: 'flex', alignItems: 'center', '&:hover': { color: '#059669' } }}
                                >
                                    <DoneAllIcon sx={{ fontSize: 14, mr: 0.5 }} /> Mark all
                                </Typography>
                            </Box>
                            {siteVisits.map((item) => {
                                const isRead = item.is_read || 0;
                                const dateColor = getDateColor(item.site_visit_date, isRead);
                                const name = item.lead?.name || 'N/A';
                                const remarks = item.site_visit_remarks || item.remarks || 'No remarks';

                                return (
                                    <Box key={item.id} sx={{ 
                                        display: 'flex', 
                                        alignItems: 'flex-start', 
                                        mb: 2, 
                                        gap: 1.5, 
                                        justifyContent: 'space-between',
                                        p: 1.5,
                                        borderRadius: '12px',
                                        bgcolor: isRead === 0 ? '#FFFFFF' : '#f9fafb',
                                        border: '1px solid',
                                        borderColor: isRead === 0 ? '#e0e0e0' : 'transparent',
                                        boxShadow: isRead === 0 ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                        opacity: isRead === 1 ? 0.8 : 1
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: isRead === 0 ? '#dcfce7' : '#f1f1f5', color: isRead === 0 ? '#10b981' : '#6b7280', fontSize: '0.8rem', fontWeight: 600 }}>
                                                {name.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: dateColor, fontSize: '0.8rem', mb: 0.5 }}>
                                                    {formatDisplayDate(item.site_visit_date)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ 
                                                    fontWeight: 600, 
                                                    color: '#1F2937', 
                                                    mb: 0.25,
                                                    cursor: 'pointer',
                                                    '&:hover': { color: '#2563EB', textDecoration: 'underline' }
                                                }} onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(`/dashboard/lead-details/${item.lead_id}`, '_blank');
                                                    setOpen(false);
                                                }}>
                                                    {name}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#6B7280', fontStyle: 'italic', display: 'block' }}>
                                                    "{remarks}"
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {isRead === 0 && (
                                            <Tooltip title="Mark as read">
                                                <IconButton size="small" onClick={() => handleMarkAsRead('sitevisit', item.id)} sx={{ color: '#10b981', mt: 0.5 }}>
                                                    <CheckCircleOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {followUps.length > 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: '#6366f1', 
                                        fontWeight: 'bold', 
                                        display: 'block' 
                                    }}
                                >
                                    Follow-ups
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    onClick={() => handleMarkAllRead('followup')}
                                    sx={{ color: '#6366f1', cursor: 'pointer', display: 'flex', alignItems: 'center', '&:hover': { color: '#4f46e5' } }}
                                >
                                    <DoneAllIcon sx={{ fontSize: 14, mr: 0.5 }} /> Mark all
                                </Typography>
                            </Box>
                            {followUps.map((item) => {
                                const isRead = item.follow_up_is_read || 0;
                                const dateColor = getDateColor(item.follow_up_date, isRead);
                                const name = item.name || 'N/A';
                                const remarks = item.follow_up_remarks || item.remarks || 'No remarks';

                                return (
                                    <Box key={item.id} sx={{ 
                                        display: 'flex', 
                                        alignItems: 'flex-start', 
                                        mb: 2, 
                                        gap: 1.5, 
                                        justifyContent: 'space-between',
                                        p: 1.5,
                                        borderRadius: '12px',
                                        bgcolor: isRead === 0 ? '#FFFFFF' : '#f9fafb',
                                        border: '1px solid',
                                        borderColor: isRead === 0 ? '#e0e0e0' : 'transparent',
                                        boxShadow: isRead === 0 ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                        opacity: isRead === 1 ? 0.8 : 1
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: isRead === 0 ? '#eef2ff' : '#f1f1f5', color: isRead === 0 ? '#6366f1' : '#6b7280', fontSize: '0.8rem', fontWeight: 600 }}>
                                                {name.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: dateColor, fontSize: '0.8rem', mb: 0.5 }}>
                                                    {formatDisplayDate(item.follow_up_date)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ 
                                                    fontWeight: 600, 
                                                    color: '#1F2937', 
                                                    mb: 0.25,
                                                    cursor: 'pointer',
                                                    '&:hover': { color: '#2563EB', textDecoration: 'underline' }
                                                }} onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(`/dashboard/lead-details/${item.id}`, '_blank');
                                                    setOpen(false);
                                                }}>
                                                    {name}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#6B7280', fontStyle: 'italic', display: 'block' }}>
                                                    "{remarks}"
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {isRead === 0 && (
                                            <Tooltip title="Mark as read">
                                                <IconButton size="small" onClick={() => handleMarkAsRead('followup', item.id)} sx={{ color: '#6366f1', mt: 0.5 }}>
                                                    <CheckCircleOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default DailyNotificationPopup;
